import type { LeadStatus } from './lead-company';

export const PAGE_SIZE = 50;

/**
 * Country buckets fold the guessed `XX?` spellings into their country. Sweden also claims
 * the rows with no country at all, so `OTHER` excludes empty (user ruling 2026-09-07).
 */
export type CountryBucket = 'SE' | 'DK' | 'NO' | 'FI' | 'OTHER' | 'ALL';

export const COUNTRY_BUCKETS: readonly CountryBucket[] = ['SE', 'DK', 'NO', 'FI', 'OTHER', 'ALL'];

/** The countries with a bucket of their own; every other non-empty value falls into `OTHER`. */
export const NAMED_COUNTRIES: readonly Exclude<CountryBucket, 'OTHER' | 'ALL'>[] = [
  'SE',
  'DK',
  'NO',
  'FI',
];

export type LeadSortKey =
  | 'signal_revenue_online'
  | 'signal_revenue_retail'
  | 'signal_ratio'
  | 'portal_products_n'
  | 'products_nonrx_n'
  | 'brands_in_portal_n'
  | 'founded_year'
  | 'name'
  | 'last_contacted'
  | 'next_action_date'
  | 'comparisons_n'
  | 'top_comparison_pct'
  | 'signal_vendors_online'
  | 'signal_vendors_retail';

export const LEAD_SORT_KEYS: readonly LeadSortKey[] = [
  'signal_revenue_online',
  'signal_revenue_retail',
  'signal_ratio',
  'portal_products_n',
  'products_nonrx_n',
  'brands_in_portal_n',
  'founded_year',
  'name',
  'last_contacted',
  'next_action_date',
  'comparisons_n',
  'top_comparison_pct',
  'signal_vendors_online',
  'signal_vendors_retail',
];

/**
 * The numeric threshold filters. Each one switches between a floor and a ceiling, because
 * either direction is a real question: "who is big enough to bother with" and "who is small
 * enough to still be winnable" are both asked of the same column.
 */
export type ThresholdKey =
  | 'signalRevenueOnline'
  | 'ratio'
  | 'brandsInPortal'
  | 'portalProducts';

export type ThresholdDirection = 'min' | 'max';

export interface Threshold {
  readonly value: number | null;
  readonly direction: ThresholdDirection;
}

export type Thresholds = Readonly<Record<ThresholdKey, Threshold>>;

export const THRESHOLD_KEYS: readonly ThresholdKey[] = [
  'signalRevenueOnline',
  'ratio',
  'brandsInPortal',
  'portalProducts',
];

/** The column each threshold filters, which is the adapter's only mapping for them. */
export const THRESHOLD_COLUMNS: Readonly<Record<ThresholdKey, string>> = {
  signalRevenueOnline: 'signal_revenue_online',
  ratio: 'signal_ratio',
  brandsInPortal: 'brands_in_portal_n',
  portalProducts: 'portal_products_n',
};

const NO_THRESHOLDS: Thresholds = {
  signalRevenueOnline: { value: null, direction: 'min' },
  ratio: { value: null, direction: 'min' },
  brandsInPortal: { value: null, direction: 'min' },
  portalProducts: { value: null, direction: 'min' },
};

export interface LeadQuery {
  readonly search: string;
  /** Empty means untouched, which is what lets the checkboxes shape the default set. */
  readonly statuses: readonly LeadStatus[];
  readonly country: CountryBucket;
  readonly notContactedYet: boolean;
  readonly hasBrandsInPortal: boolean;
  readonly hasContactEmail: boolean;
  readonly includeDoNotContact: boolean;
  readonly includeCustomersAndDemo: boolean;
  readonly thresholds: Thresholds;
  /** true = only companies in a comparison, false = only those in none, null = either. */
  readonly hasComparisons: boolean | null;
  /** Strict ceiling on top coverage, for finding the companies no comparison targets. */
  readonly belowTopComparisonPct: number | null;
  readonly sortKey: LeadSortKey;
  readonly sortAscending: boolean;
  /** Zero-based. */
  readonly page: number;
}

export const DEFAULT_LEAD_QUERY: LeadQuery = {
  search: '',
  statuses: [],
  country: 'SE',
  notContactedYet: false,
  hasBrandsInPortal: false,
  hasContactEmail: false,
  includeDoNotContact: false,
  includeCustomersAndDemo: false,
  thresholds: NO_THRESHOLDS,
  hasComparisons: null,
  belowTopComparisonPct: null,
  sortKey: 'signal_revenue_online',
  sortAscending: false,
  page: 0,
};

/**
 * The status set the query actually filters on.
 *
 * A touched multi-select wins outright; the checkboxes only shape the default set while it
 * is untouched (user ruling 2026-09-07). `notContactedYet` still contributes its
 * `last_contacted is null` half either way -- that half lives in the adapter.
 */
export function effectiveStatuses(query: LeadQuery): readonly LeadStatus[] {
  if (query.statuses.length > 0) return query.statuses;
  if (query.notContactedYet) return ['lead'];
  return query.includeCustomersAndDemo
    ? ['lead', 'contacted', 'demo', 'customer', 'past_customer']
    : ['lead', 'contacted'];
}
